import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';

interface SecureStorageContextType {
  storeSecureData: (key: string, data: any) => void;
  getSecureData: (key: string) => any;
  removeSecureData: (key: string) => void;
  clearAllSecureData: () => void;
}

const SecureStorageContext = createContext<SecureStorageContextType | undefined>(undefined);

export const useSecureStorage = () => {
  const context = useContext(SecureStorageContext);
  if (context === undefined) {
    throw new Error('useSecureStorage must be used within a SecureStorageProvider');
  }
  return context;
};

// Session-based storage that clears on logout
const sessionStorage = new Map<string, any>();

export const SecureStorageProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();

  const storeSecureData = (key: string, data: any) => {
    if (!user) return;
    
    // Only store non-sensitive data in session memory
    const secureKey = `${user.id}_${key}`;
    sessionStorage.set(secureKey, data);
  };

  const getSecureData = (key: string) => {
    if (!user) return null;
    
    const secureKey = `${user.id}_${key}`;
    return sessionStorage.get(secureKey) || null;
  };

  const removeSecureData = (key: string) => {
    if (!user) return;
    
    const secureKey = `${user.id}_${key}`;
    sessionStorage.delete(secureKey);
  };

  const clearAllSecureData = () => {
    sessionStorage.clear();
    
    // Also clear any legacy localStorage entries
    const keysToRemove = Object.keys(localStorage).filter(key => 
      key.startsWith('secure_') || key.startsWith('encrypted_') || key.startsWith('business_')
    );
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };

  // Auto-cleanup on user logout
  useEffect(() => {
    if (!user) {
      clearAllSecureData();
    }
  }, [user]);

  const value = {
    storeSecureData,
    getSecureData,
    removeSecureData,
    clearAllSecureData,
  };

  return (
    <SecureStorageContext.Provider value={value}>
      {children}
    </SecureStorageContext.Provider>
  );
};